import {useState} from 'react';
import {CustomerSystem} from '@/store/customerFile.ts';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody} from '@/components/ui/dialog.tsx';
import {Button} from '@/components/ui/button.tsx';
import {Input} from '@/components/ui/input.tsx';
import {Label} from '@/components/ui/label.tsx';
import {ArrowsRightLeftIcon, ExclamationTriangleIcon, CheckCircleIcon} from '@heroicons/react/24/outline';
import {Spinner} from '@/components/ui/spinner.tsx';

interface Props {
    open: boolean;
    onClose: () => void;
    fromSystem: CustomerSystem;
    customerId?: number;
}

interface ValidateResult {
    school_name: string;
    system_name: string | null;
    is_same_customer: boolean;
    school_id: number;
}

type Step = 'phone' | 'confirm';

export function TransferUnitsModal({open, onClose, fromSystem}: Props) {
    const [step, setStep] = useState<Step>('phone');
    const [targetPhone, setTargetPhone] = useState('');
    const [amount, setAmount] = useState('');
    const [validating, setValidating] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [validated, setValidated] = useState<ValidateResult | null>(null);
    const [transferring, setTransferring] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    function reset() {
        setStep('phone');
        setTargetPhone('');
        setAmount('');
        setValidating(false);
        setPhoneError('');
        setValidated(null);
        setTransferring(false);
        setDone(false);
        setError('');
    }

    function handleClose() {
        reset();
        onClose();
    }

    async function handleValidatePhone() {
        setPhoneError('');
        if (!targetPhone.trim()) {
            setPhoneError('נא להזין מספר טלפון');
            return;
        }
        const amountNum = parseInt(amount, 10);
        if (!amount || isNaN(amountNum) || amountNum <= 0) {
            setPhoneError('נא להזין כמות יחידות תקינה');
            return;
        }
        if (amountNum > fromSystem.balance_unit) {
            setPhoneError(`יתרת היחידות הזמינה: ${fromSystem.balance_unit.toLocaleString('he-IL')}`);
            return;
        }
        setValidating(true);
        try {
            const resp = await fetch('/api/customer-file/validate-transfer-phone/', {
                method: 'POST',
                headers: {'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken()},
                body: JSON.stringify({phone: targetPhone.trim()}),
            });
            const data = await resp.json();
            if (!resp.ok || !data.status) {
                setPhoneError(data.message || 'מספר הטלפון לא נמצא במערכת');
                return;
            }
            setValidated(data.data);
            setStep('confirm');
        } catch {
            setPhoneError('שגיאת רשת, נסה שוב');
        } finally {
            setValidating(false);
        }
    }

    async function handleTransfer() {
        if (!validated) return;
        setTransferring(true);
        setError('');
        try {
            const resp = await fetch('/api/customer-file/transfer-units/', {
                method: 'POST',
                headers: {'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken()},
                body: JSON.stringify({
                    from_school_id: fromSystem.id,
                    to_school_id: validated.school_id,
                    amount: parseInt(amount, 10),
                }),
            });
            const data = await resp.json();
            if (!resp.ok || !data.status) {
                setError(data.message || 'שגיאה בביצוע ההעברה');
                return;
            }
            setDone(true);
        } catch {
            setError('שגיאת רשת, נסה שוב');
        } finally {
            setTransferring(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent dir="rtl" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ArrowsRightLeftIcon className="w-5 h-5 text-blue-600" />
                        העברת יחידות — {fromSystem.name}
                    </DialogTitle>
                </DialogHeader>

                {done ? (
                    <DialogBody className="flex flex-col items-center gap-3 py-6">
                        <CheckCircleIcon className="w-12 h-12 text-green-500" />
                        <p className="text-sm font-medium text-gray-800">ההעברה בוצעה בהצלחה</p>
                        <p className="text-xs text-gray-500">{parseInt(amount, 10).toLocaleString('he-IL')} יחידות הועברו אל {validated?.school_name}</p>
                        <Button onClick={handleClose} className="mt-2">סגור</Button>
                    </DialogBody>
                ) : step === 'phone' ? (
                    <DialogBody className="space-y-4 py-2">
                        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700">
                            יתרת יחידות זמינה במערכת <strong>{fromSystem.name}</strong>: <strong>{fromSystem.balance_unit.toLocaleString('he-IL')}</strong>
                        </div>
                        <div className="space-y-1.5">
                            <Label>מספר טלפון של המערכת אליה תרצו להעביר יחידות</Label>
                            <Input
                                dir="ltr"
                                placeholder="073345...."
                                value={targetPhone}
                                onChange={(e) => setTargetPhone(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleValidatePhone()}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>כמות יחידות להעברה</Label>
                            <Input
                                type="number"
                                min={1}
                                max={fromSystem.balance_unit}
                                placeholder="100"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />
                        </div>
                        {phoneError && (
                            <p className="text-xs text-red-600 flex items-center gap-1">
                                <ExclamationTriangleIcon className="w-3.5 h-3.5" />{phoneError}
                            </p>
                        )}
                    </DialogBody>
                ) : validated ? (
                    <DialogBody className="space-y-4 py-2">
                        <div className={`rounded-xl border p-4 text-sm space-y-2 ${validated.is_same_customer ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                            <p><strong>טלפון יעד:</strong> {validated.school_name}</p>
                            {validated.is_same_customer && validated.system_name && (
                                <p><strong>מערכת:</strong> {validated.system_name}</p>
                            )}
                            {!validated.is_same_customer && (
                                <p className="text-amber-700 text-xs">המוסד שייך לתיק לקוח אחר</p>
                            )}
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm space-y-1.5">
                            <p><strong>ממערכת:</strong> {fromSystem.name}</p>
                            <p><strong>יתרה נוכחית:</strong> {fromSystem.balance_unit.toLocaleString('he-IL')} יחידות</p>
                            <p><strong>כמות להעברה:</strong> {parseInt(amount, 10).toLocaleString('he-IL')} יחידות</p>
                            <p><strong>יתרה לאחר ההעברה:</strong> {(fromSystem.balance_unit - parseInt(amount, 10)).toLocaleString('he-IL')} יחידות</p>
                        </div>
                        {error && (
                            <p className="text-xs text-red-600 flex items-center gap-1">
                                <ExclamationTriangleIcon className="w-3.5 h-3.5" />{error}
                            </p>
                        )}
                    </DialogBody>
                ) : null}

                {!done && (
                    <DialogFooter className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={handleClose}>ביטול</Button>
                        {step === 'phone' ? (
                            <Button onClick={handleValidatePhone} disabled={validating}>
                                {validating ? <Spinner size="sm" /> : 'המשך'}
                            </Button>
                        ) : (
                            <>
                                <Button variant="outline" onClick={() => setStep('phone')}>חזרה</Button>
                                <Button onClick={handleTransfer} disabled={transferring}>
                                    {transferring ? <Spinner size="sm" /> : 'אישור ביצוע'}
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}

function getCsrfToken(): string {
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? match[1] : '';
}
